import cn from 'classnames';
import parse from 'html-react-parser';
import { FC } from 'react';

import { ArrowRightIcon, Button } from '@catawiki/cata-style';

import {
  SanitizeMode,
  getCleanInput,
  isBuilderStringPresent,
  someBuilderStringPresent,
} from '@/shared/text';
import { BuilderString, CtaVariant, TopicTone } from '@/shared/types';
import { IconName } from '@/shared/constants/icons';

import { FlatMediaInput, Media, MediaType } from '../Media';
import styles from './Topic.module.scss';
import { TopicLead } from './TopicLead';

export interface TopicRow extends FlatMediaInput {
  number?: BuilderString;
  icon?: IconName;
  tone?: TopicTone;
  title: BuilderString;
  subtitle: BuilderString;
  description: BuilderString;
  ctaText?: BuilderString;
  ctaUrl?: BuilderString;
}

const toneStyles: Record<
  TopicTone,
  { surfaceClassName?: string; textClassName?: string }
> = {
  neutral: {},
  success: {
    surfaceClassName: 'tw:bg-success-secondary',
    textClassName: 'tw:text-success-primary',
  },
  warning: {
    surfaceClassName: 'tw:bg-warning-secondary',
    textClassName: 'tw:text-warning-primary',
  },
  error: {
    surfaceClassName: 'tw:bg-critical-secondary',
    textClassName: 'tw:text-critical-primary',
  },
};

interface Props extends TopicRow {
  asBox: boolean;
  backgroundColorClassName: string;
  ctaVariant: CtaVariant;
  className?: string;
  descriptionSanitizeMode?: SanitizeMode;
  fullCardLink?: boolean;
  locale: string;
}

const TEXT_SPACING = 'u-m-t-sm-m u-m-t-md-m u-m-t-lg-xl';

export const Topic: FC<Props> = ({
  title,
  subtitle,
  description,
  ctaUrl,
  ctaVariant,
  ctaText,
  descriptionSanitizeMode = SanitizeMode.SINGLE_LINE,
  backgroundColorClassName,
  asBox = false,
  icon,
  number,
  tone = 'neutral',
  locale,
  className,
  fullCardLink = false,

  mediaType,
  mediaCaption,
  imageUrl,
  imageLink,
  imageLinkTarget,
  videoUrl,
  videoAutoPlay,
  youtubeUrl,
  youtubeDisabledDialog,
}) => {
  const { surfaceClassName, textClassName } = toneStyles[tone];
  const descriptionTextClassName = textClassName || 'tw:text-neutral-secondary';
  const hasFullCardLink =
    fullCardLink &&
    isBuilderStringPresent(title, locale) &&
    isBuilderStringPresent(ctaUrl, locale);
  const textSpacing = hasFullCardLink ? 'u-m-t-xs' : TEXT_SPACING;

  return (
    <section className={className}>
      <div
        className={cn(
          surfaceClassName || backgroundColorClassName,
          textClassName,
          {
            [styles.container]: true,
            'u-p-xl u-p-lg-xxl-4': asBox,
          }
        )}
      >
        <Media
          className={cn(styles.imageContainer, {
            [styles.cardLinkLayer]: hasFullCardLink,
          })}
          containerClassName={styles.image}
          locale={locale}
          mediaType={mediaType}
          mediaCaption={mediaCaption}
          image={imageUrl}
          imageLink={imageLink}
          imageLinkTarget={imageLinkTarget}
          video={{
            source: videoUrl || '',
            autoPlay: videoAutoPlay,
            poster: imageUrl,
          }}
          youtube={{
            url: youtubeUrl || '',
            poster: imageUrl || '',
            disabledDialog: youtubeDisabledDialog,
          }}
        />
        {hasFullCardLink && (
          <>
            <a
              className={cn(
                styles.fullCardLink,
                textClassName || 'tw:text-brand-primary'
              )}
              href={getCleanInput(
                locale,
                SanitizeMode.SINGLE_LINE_TEXT_ONLY,
                ctaUrl
              )}
              aria-label={getCleanInput(
                locale,
                SanitizeMode.SINGLE_LINE_TEXT_ONLY,
                title
              )}
            />
            <ArrowRightIcon
              aria-hidden="true"
              className={cn(
                styles.fullCardLinkIndicator,
                'tw:h-s tw:w-s',
                textClassName || 'tw:text-brand-primary'
              )}
            />
          </>
        )}
        <div
          className={cn(
            { 'u-m-t-xl': mediaType !== MediaType.NONE },
            { [styles.cardLinkLayer]: hasFullCardLink },
            { 'tw:pr-xl': hasFullCardLink }
          )}
        >
          <TopicLead
            icon={icon}
            number={number}
            compact={hasFullCardLink}
            colorClassName={textClassName}
            locale={locale}
          />
          {isBuilderStringPresent(title, locale) ? (
            <div
              className={cn(
                'u-typography-h3',
                textClassName,
                someBuilderStringPresent([number], locale) && textSpacing
              )}
            >
              {parse(getCleanInput(locale, SanitizeMode.SINGLE_LINE, title))}
            </div>
          ) : null}
          {isBuilderStringPresent(subtitle, locale) ? (
            <div
              className={cn(
                'u-typography-h6',
                descriptionTextClassName,
                someBuilderStringPresent([number, title], locale) && textSpacing
              )}
            >
              {parse(getCleanInput(locale, SanitizeMode.SINGLE_LINE, subtitle))}
            </div>
          ) : null}
          {isBuilderStringPresent(description, locale) ? (
            <div
              className={cn(
                descriptionTextClassName,
                'u-typography-h7',
                someBuilderStringPresent([number, title, subtitle], locale) &&
                  textSpacing,
                {
                  [styles.descriptionWithLists]:
                    descriptionSanitizeMode === SanitizeMode.MULTIPLE_LINE,
                }
              )}
            >
              {parse(
                getCleanInput(locale, descriptionSanitizeMode, description, {
                  allowLists:
                    descriptionSanitizeMode === SanitizeMode.MULTIPLE_LINE,
                })
              )}
            </div>
          ) : null}
          {ctaVariant === 'none' || hasFullCardLink ? null : (
            <Button
              className={cn({
                [textSpacing]: someBuilderStringPresent(
                  [number, title, subtitle, description],
                  locale
                ),
              })}
              variant={ctaVariant}
              as="a"
              href={getCleanInput(
                locale,
                SanitizeMode.SINGLE_LINE_TEXT_ONLY,
                ctaUrl
              )}
            >
              {getCleanInput(
                locale,
                SanitizeMode.SINGLE_LINE_TEXT_ONLY,
                ctaText
              )}
            </Button>
          )}
        </div>
      </div>
    </section>
  );
};
